import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/** Hardcoded fallbacks — keep in sync with backend `DEFAULT_TIPS` so the UI
 *  never crashes if the API is unreachable. Server defaults are authoritative. */
const FALLBACK = {
  chat:       { enabled: true, message: "First time chatting with AI? I can suggest where to start.",  prefill: "What can I ask the AI Chat to do for me?" },
  knowledge:  { enabled: true, message: "Want to teach the AI your business?",                          prefill: "What documents should I upload to the Knowledge Base?" },
  leads:      { enabled: true, message: "Want to import your prospect list? I can walk you through it.", prefill: "How do I import my leads?" },
  sequences:  { enabled: true, message: "Ready to set up your first outreach campaign?",               prefill: "How do I create my first email sequence?" },
  content:    { enabled: true, message: "Want to write your first piece of content?",                  prefill: "Which generator should I start with — blog, LinkedIn, email, or ad?" },
  agents:     { enabled: true, message: "Ready to add a chatbot to your website?",                      prefill: "How do I set up a website chatbot?" },
  assistant:  { enabled: true, message: "I can show you how this works.",                              prefill: "What does the Executive Assistant do for me?" },
};

const SamTipsContext = createContext({ tips: FALLBACK, refresh: () => {} });

export function SamTipsProvider({ children }) {
  const { user } = useAuth();
  const [tips, setTips] = useState(FALLBACK);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/sam-tips/public");
      const merged = { ...FALLBACK };
      for (const k of Object.keys(FALLBACK)) {
        if (r.data && r.data[k]) merged[k] = { ...FALLBACK[k], ...r.data[k] };
      }
      setTips(merged);
    } catch (_) {
      // Network/auth issue → keep whatever we have (fallback or stale)
    }
  }, []);

  // Fetch once when the user becomes available
  useEffect(() => {
    if (!user) return;
    refresh();
  }, [user, refresh]);

  return (
    <SamTipsContext.Provider value={{ tips, refresh }}>
      {children}
    </SamTipsContext.Provider>
  );
}

export function useSamTip(moduleKey) {
  const { tips } = useContext(SamTipsContext);
  return tips[moduleKey] || FALLBACK[moduleKey] || { enabled: false, message: "", prefill: "" };
}

export function useSamTipsRefresh() {
  return useContext(SamTipsContext).refresh;
}

export { FALLBACK as SAM_TIP_FALLBACKS };
